import React from 'react';
import { Building2, Flame, Globe, Users } from 'lucide-react';

const About: React.FC = () => {
  return (
    <section id="about" className="bg-gray-50 dark:bg-gray-900 py-20">
      <div className="max-w-6xl mx-auto px-6">
      <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-100 mb-8">
          About Me
        </h1>

        {/* Introduction Section */}
        <div className="flex flex-col lg:flex-row gap-8 items-start">
          {/* Profile Image */}
          <img
            src="/assets/images/Picture1.png"
            alt="Ryan Hardesty Lewis"
            className="rounded-lg shadow-md w-48 lg:w-56 flex-shrink-0"
          />
          {/* Text Content */}
          <div>
            <p className="text-sm text-gray-700 dark:text-gray-300 mb-4 leading-relaxed">
              <b>Welcome!</b> I’m Ryan Hardesty Lewis, a first-year PhD student at MIT, where
              I’m passionate about using <b>technology</b> to create smarter, more equitable
              cities. My work combines cutting-edge research with practical, impactful applications
              in areas like <b>urban informatics</b>, <b>sustainability</b>, and <b>AI-driven solutions</b>.
            </p>
            <p className="text-sm text-gray-700 dark:text-gray-300 mb-4 leading-relaxed">
              I’ve collaborated with local governments (New York, Houston, Austin), startups (Tubular.network), and global research labs (MITRE) to
              integrate digital tools into public systems. Whether it's working on a <b>fire and
              smoke digital twin</b> for the City of Austin or leveraging AI to improve food access in Houston,
              I believe in the power of <b>technology to improve lives.</b>
            </p>
            <p className="text-sm text-gray-700 dark:text-gray-300 mb-4 leading-relaxed">
              I graduated with honors from the <b>University of Texas at Austin</b> in under two years, earning
              a degree in <b>Mathematics</b> and two certificates in <b>Computational Science and Engineering</b> and
              <b className="ml-1">Elements of Computing</b>. At UT Austin, I worked with Dr. Junfeng Jiao in the <b>Urban
              Information Lab</b>, contributing to innovative projects that addressed real-world
              challenges. I've worked on cutting-edge solutions tackling some of New York City's urban and climate issues by collaborating with 
              nonprofits like <b>New Yorkers for Parks</b> and <b>Tackle Hunger</b>, bridging technology and community for meaningful impact.
            </p>
          </div>
        </div>

        {/* Vision Section */}
        <div className="mt-8">
        <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-6">
            My Vision
        </h2>
        <div className="flex flex-col lg:flex-row items-center gap-8">
            <div>
            <p className="text-sm text-gray-700 dark:text-gray-300 mb-4 leading-relaxed">
                Growing up as the son of a Colombian immigrant, I spoke only Spanish at home and relied on
                public systems designed to support those in need. These systems, while essential,
                often fell short of providing the equity and efficiency my family and others deserved.
                This experience instilled in me a drive to <b>reimagine public systems</b>—to make them
                smarter, more inclusive, and better equipped to serve diverse communities.
            </p>
            <p className="text-sm text-gray-700 dark:text-gray-300 mb-4 leading-relaxed">
                My heritage is a cornerstone of my work. I’m inspired by the resilience of immigrant
                communities and the power of technology to bridge systemic gaps. From collaborating
                with first responders during a prescribed burn{" "}
                to improving urban accessibility to services like food banks and emergency response, my vision is to create systems that empower
                individuals and foster connections.
            </p>
            <p className="text-sm text-gray-700 dark:text-gray-300 mb-6 leading-relaxed">
            I believe cities are the heartbeat of human connection, where culture, history, 
            and innovation converge. They hold the power to inspire, to shelter, and to challenge us 
            to think beyond ourselves. A truly great city doesn’t just function—it thrives when it 
            uplifts its most vulnerable, celebrates its diversity, and evolves to meet the 
            ever-changing needs of its people. Technology is the key to unlocking this potential.
            </p>
            </div>
            <div className="flex-shrink-0">
            <img
                src="/assets/images/fire.png"
                alt="Collaborating with the Austin Fire Department"
                className="rounded-lg shadow-md w-96"
            />
            <span className="block text-xs text-gray-500 italic mt-2">
                Working with the Austin Fire Department during a prescribed burn.
            </span>
            </div>
        </div>
        <div className="flex flex-col lg:flex-row items-center gap-8 mt-10">
        <div className="flex-shrink-0">
            <img
            src="/assets/images/pres.jpg"
            alt="Meeting President Iván Duque of Colombia"
            className="rounded-lg shadow-md w-96 lg:w-112"
            />
            <span className="block text-xs text-gray-500 italic mt-2">
            Meeting President Iván Duque of Colombia.
            </span>
        </div>
        <div>
            <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
            As someone deeply passionate about improving cities, I’ve had the opportunity to work
            alongside leaders from around the globe. These experiences have taught me that cities,
            in their purest form, are a reflection of the hopes, needs, and beliefs of their citizens.
            However, too often, public systems fail to meet these expectations, becoming slow,
            inequitable, and out of touch with the communities they serve.
            </p>
            <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed mt-4">
            Through collaboration with decision-makers, technologists, and local communities, I aim to
            transform this dynamic. My work focuses on using technology to <b>streamline response times</b>, 
            improve <b>accessibility</b>, and build systems that feel less like rigid bureaucracy and more like
            a living conversation—one where the city truly listens to its people. Whether it’s
            optimizing emergency services or ensuring equitable access to resources, I see cities not
            as static infrastructures but as ecosystems capable of evolving alongside their citizens.
            </p>
            <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed mt-4">
            This belief is what drives me to engage with a diverse range of voices. I’ve come to
            understand that meaningful innovation doesn’t happen in isolation—it requires a tapestry of
            perspectives, from local advocates to global leaders. Technology, when wielded
            thoughtfully, has the power to turn these collaborations into solutions that work for
            everyone, from immigrant to <i>indígena</i>.
            </p>
        </div>
        </div>
        </div>

        {/* Icons Section */}
        <div className="mt-16">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
            <div className="flex flex-col items-center">
              <Building2 className="w-10 h-10 text-blue-600 mb-2" />
              <p className="text-xs text-gray-600 dark:text-gray-400 text-center">
                Smarter Cities
              </p>
            </div>
            <div className="flex flex-col items-center">
              <Flame className="w-10 h-10 text-red-600 mb-2" />
              <p className="text-xs text-gray-600 dark:text-gray-400 text-center">
                Climate Resilience
              </p>
            </div>
            <div className="flex flex-col items-center">
              <Users className="w-10 h-10 text-green-600 mb-2" />
              <p className="text-xs text-gray-600 dark:text-gray-400 text-center">
                Community Building
              </p>
            </div>
            <div className="flex flex-col items-center">
              <Globe className="w-10 h-10 text-purple-600 mb-2" />
              <p className="text-xs text-gray-600 dark:text-gray-400 text-center">
                Global Impact
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
